// src/pages/ParentProfilePage.jsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import ParentEditForm from '../components/parent/ParentEditForm';
import '../styles/forms.css';
import { updateUserProfile } from '../services/firestore';

const ParentProfilePage = () => {
  const { id } = useParams();
  const { currentUser } = useAuth();
  const [parent, setParent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Get the user ID from the URL or use the current user's ID
  const userId = id || currentUser?.uid;

  // Fetch parent data
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      setError('Invalid user ID');
      return;
    }

    const fetchParent = async () => {
      try {
        const parentDoc = await getDoc(doc(db, 'parents', userId));
        
        if (parentDoc.exists()) {
          setParent({ id: parentDoc.id, ...parentDoc.data() });
        } else {
          setError('Parent not found');
        }
      } catch (err) {
        console.error('Error fetching parent:', err);
        setError('Failed to load parent information');
      } finally {
        setLoading(false);
      }
    };
    
    fetchParent();
  }, [userId]);

  // Handle profile update
  const handleProfileUpdate = async (updatedProfile) => {
    try {
      await updateUserProfile('parents', parent.id, updatedProfile);
      setParent(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          ...updatedProfile
        }
      }));
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile');
    }
  };
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading parent profile...</p>
      </div>
    );
  }
  
  if (error || !parent) {
    return (
      <div className="error-container">
        <h2>Error</h2>
        <p>{error || 'Failed to load parent profile'}</p>
      </div>
    );
  }
  
  const parentProfile = parent.profile || {};
  
  return (
    <div className="parent-profile-page">
      <div className="profile-header">
        <div className="header-content">
          <h1>{parent.displayName || 'Parent'}</h1>
        </div>
        
        {currentUser && currentUser.uid === parent.uid && (
          <div className="header-actions">
            <button 
              className="edit-button"
              onClick={() => setIsEditing(!isEditing)}
            >
              {isEditing ? 'Cancel Edit' : 'Edit Profile'}
            </button>
          </div>
        )}
      </div>

      {isEditing ? (
        <ParentEditForm
          profile={parentProfile}
          onSave={handleProfileUpdate}
          onCancel={() => setIsEditing(false)}
        />
      ) : (
        <div className="profile-content">
          <div className="profile-details">
            <div className="details-grid">
              <div className="details-section">
                <h2>Contact Information</h2>
                {parentProfile.phone && (
                  <p><strong>Phone:</strong> {parentProfile.phone}</p>
                )}
                <p><strong>Email:</strong> {parent.email || 'Email not provided'}</p>
              </div>

              <div className="details-section">
                <h2>Service Preferences</h2>
                <p><strong>Preferred Service Type:</strong> {
                  parentProfile.serviceType === 'nanny' ? 'Nanny' :
                  parentProfile.serviceType === 'provider' ? 'Daycare Provider' :
                  parentProfile.serviceType === 'both' ? 'Nanny & Daycare Provider' :
                  'Not specified'
                }</p>
                
                {parentProfile.searchRadius && (
                  <p><strong>Search Radius:</strong> {parentProfile.searchRadius} km</p>
                )}
              </div>

              <div className="details-section">
                <h2>Activity Preferences</h2>
                {parentProfile.activityPreferences && parentProfile.activityPreferences.length > 0 ? (
                  <div className="activity-preferences">
                    {parentProfile.activityPreferences.map(activity => (
                      <span className="activity-tag" key={activity}>{activity}</span>
                    ))}
                  </div>
                ) : (
                  <p>No activity preferences specified</p>
                )}
              </div>

              <div className="details-section">
                <h2>Languages</h2>
                {parentProfile.languages && parentProfile.languages.length > 0 ? (
                  <div className="languages-list">
                    {parentProfile.languages.map(language => (
                      <span className="language-tag" key={language}>{language}</span>
                    ))}
                  </div>
                ) : (
                  <p>No languages specified</p>
                )}
              </div>

              <div className="details-section">
                <h2>Children</h2>
                {parentProfile.children && parentProfile.children.length > 0 ? (
                  <div className="children-list">
                    {parentProfile.children.map((child, index) => (
                      <div className="child-item" key={index}>
                        <h3>{child.name}</h3>
                        <p><strong>Age:</strong> {child.age}</p>
                        {child.gender && <p><strong>Gender:</strong> {child.gender}</p>}
                        {child.specialNeeds && <p><strong>Special Needs:</strong> {child.specialNeeds}</p>}
                        {child.allergies && <p><strong>Allergies:</strong> {child.allergies}</p>}
                        {child.interests && child.interests.length > 0 && (
                          <div className="child-interests">
                            <strong>Interests:</strong>
                            {child.interests.map(interest => (
                              <span className="interest-tag" key={interest}>{interest}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>No children information provided</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentProfilePage;